import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Input } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import { allowEnterPress } from '../utils'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'

const decimalHasValue = rawNum =>
  rawNum === 0 ||
  rawNum === '0' ||
  (!!rawNum && rawNum !== '' && !Number.isNaN(Number(rawNum)))

const buildDecimalSubmission = (props, rawNum) => {
  const { question, id } = props
  const { label, type, attributes } = question
  const { required, unit } = attributes
  const hasValue = decimalHasValue(rawNum)
  const answer = hasValue
    ? [
      {
        label,
        value: rawNum.toString(),
        question: id,
        unit,
      },
    ]
    : []
  return {
    id,
    label,
    name: id,
    type,
    required,
    state: hasValue ? 'ANSWERED' : 'SKIPPED',
    answer,
  }
}

const persistDecimalSubmission = (props, rawNum) => {
  try {
    const { webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    completeQuestion(
      buildDecimalSubmission(props, rawNum),
      webSurvey.order?.[0],
    )
  } catch (e) {
    console.warn('Decimal autosave failed', { error: e })
  }
}

const completeDecimalQuestion = (props, num) => {
  const {
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const submission = buildDecimalSubmission(props, num)
  const currentAnswer = { [id]: submission.answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

function Decimal(props) {
  const {
    className,
    currentSurvey,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    forwardedRef,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
  } = props

  const { attributes, label, input_validation } = question
  const { decimal_places, max, min } = input_validation || {}

  const [num, setNum] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (storedAnswer?.[0]?.value) {
      const storedValue = storedAnswer[0].value
      setNum(storedValue)
      if (!props.suppressAutoAdvance) {
        completeDecimalQuestion(props, storedValue)
      } else {
        persistDecimalSubmission(props, storedValue)
      }
    }
  }, [])

  const validateInput = value => {
    if (value == null || value === '' || !input_validation) return ''

    const numericValue = Number(value)
    if (Number.isNaN(numericValue)) {
      return strings?.invalid_number_error ?? 'Please enter a valid number'
    }

    if (min != null && numericValue < min) {
      return strings?.min_value_error ?? `Value must be at least ${min}`
    }

    if (max != null && numericValue > max) {
      return strings?.max_value_error ?? `Value must be at most ${max}`
    }

    return ''
  }

  const formatDecimalPlaces = value => {
    if (value == null || value === '' || !question?.input_validation)
      return value

    const { decimal_places, max } = question.input_validation
    const stringValue = value.toString()

    // If max is defined, limit numeric input length (allow +2 digits +decimal_places beyond max)
    if (max != null && !isNaN(Number(value))) {
      const maxLength =
        max.toString().replace('.', '').length + 2 + decimal_places
      const valueDigits = stringValue.replace('.', '').length

      if (valueDigits > maxLength) {
        // Prevent further typing beyond allowed extra digits
        return stringValue.slice(0, stringValue.length - 1)
      }
    }

    // Limit decimal places (truncate excess decimals)
    if (decimal_places != null) {
      const [integerPart, decimalPart = ''] = stringValue.split('.')
      if (decimalPart && decimalPart.length > decimal_places) {
        // Truncate to allowed decimal places
        return `${integerPart}.${decimalPart.substring(0, decimal_places)}`
      }
    }

    return value
  }

  const onInput = rawValue => {
    setAllRequiredAnswered(false, id)

    const formatted = formatDecimalPlaces(rawValue)
    const error = validateInput(formatted)

    setError(error)
    setNum(formatted)

    if (error) return
    persistDecimalSubmission(props, formatted)
  }

  const handleCompletion = () => {
    const validationError = validateInput(num)
    if (validationError) {
      setError(validationError)
      return false
    }
    completeDecimalQuestion(props, num)
    return true
  }

  const getStepValue = () =>
    decimal_places != null ? 1 / 10 ** decimal_places : 0.1

  const { required, unit } = attributes
  const _className = className ? ` ${className}` : ''
  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div
          className={`survey-question numeric${_className}`}
          ref={forwardedRef}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label),
              }}
            />
          </p>
          <div className='input-units-row'>
            <Input
              inputClassName='numeric'
              inputProps={{
                inputMode: 'decimal',
                onKeyPress: e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (allowEnterPress(required, num) && !error) {
                      completeDecimalQuestion(props, num.toString())
                    }
                  }
                },
                pattern: '[0-9.]*',
                min: min ?? undefined,
                max: max ?? undefined,
              }}
              onChange={value => onInput(value)}
              placeholder={strings.type_in_value_placeholder}
              type='number'
              step={getStepValue()}
              value={num}
              hasError={!!error}
              errorText={error}
            />
            {unit && (
              <span className='units'>
                <b>{unit}</b>
              </span>
            )}
          </div>
          {!isSibling && (
            <QuestionFooter
              completed={decimalHasValue(num)}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={handleCompletion}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
}

const ForwardedDecimal = React.forwardRef((props, ref) => (
  <Decimal {...props} forwardedRef={ref} />
))

export default ForwardedDecimal

Decimal.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
}
